import { DRAWING_DEFAULT_COLOR, DRAWING_DEFAULT_WIDTH_PX } from "./drawingConstants";
import { copyDrawingItem, createDrawingItem } from "./drawingItem";

const ID_PREFIX = "drawing-";

/**
 * State of the drawings of the user: the list, the selected drawing and the
 * listeners of the changes.
 *
 * @param {object} params
 * @param {{ palette: object }} params.theme
 * @param {() => void} params.invalidate - schedules a frame
 * @param {() => void} params.redrawText - redraws the text layer only
 */
export const createDrawingsModel = ({ theme, invalidate, redrawText }) => {
  const listeners = new Set();
  const colorOf = (name) => theme.palette[name].css;
  const defaultStyle = { color: colorOf(DRAWING_DEFAULT_COLOR), width: DRAWING_DEFAULT_WIDTH_PX };
  const styleOfTool = (tool) => ({ ...defaultStyle, ...tool.style?.defaults(colorOf) });
  let items = [];
  let selectedId = null;
  let nextNumber = 1;

  const describe = () => ({ drawings: items.map(copyDrawingItem), selectedId });

  const notify = () => {
    const state = describe();

    listeners.forEach((listener) => listener(state));
  };

  const changed = () => {
    notify();
    invalidate();
  };

  const add = (type, points, style) => {
    const item = createDrawingItem(`${ID_PREFIX}${nextNumber}`, type, points, style);

    items = [...items, item];
    nextNumber += 1;

    return item;
  };

  const clear = () => {
    items = [];
    selectedId = null;
  };

  const select = (id) => {
    if (id === selectedId) return;

    selectedId = id;
    notify();
    redrawText();
  };

  return {
    invalidate,

    /** @returns {object} the full style of a new drawing of the tool */
    styleOfTool,

    /** Tells the listeners and redraws, e.g. after a drawing was moved. */
    changed,

    get items() {
      return items;
    },

    get selectedId() {
      return selectedId;
    },

    /** @param {string|null} id - null removes the selection */
    select,

    /** Puts a new drawing on the list, the caller tells the listeners. */
    add,

    /** Forgets the list and the selection, the caller tells the listeners: another list follows. */
    clear,

    /** @param {(state: { drawings: object[], selectedId: string|null }) => void} listener */
    subscribe: (listener) => {
      listeners.add(listener);
      listener(describe());

      return () => listeners.delete(listener);
    },

    /** @returns {object[]} copies of the drawings in the order they were drawn */
    list: () => items.map(copyDrawingItem),

    /** @returns {boolean} false for an unknown id */
    remove: (id) => {
      if (!items.some((item) => item.id === id)) return false;

      items = items.filter((item) => item.id !== id);
      if (selectedId === id) selectedId = null;
      changed();

      return true;
    },

    /** Removes every drawing with one event. */
    removeAll: () => {
      if (!items.length) return;

      clear();
      changed();
    },

    destroy: () => listeners.clear(),
  };
};
